import React from 'react';
import { useAuth, useProtectedNavigate } from '@/App';
import Navigation from '@/components/Navigation';
import { BookOpen, FileText, AlertTriangle, CheckSquare, HardHat, Wrench, Hammer } from 'lucide-react';

const resources = [
  {
    category: 'Building Defects',
    icon: AlertTriangle,
    items: [
      'Common structural defects and identification',
      'Water damage assessment guide',
      'Foundation crack severity guide',
      'Roofing defect checklist'
    ]
  },
  {
    category: 'Specialist Services',
    icon: CheckSquare,
    items: [
      'Roofing reports',
      'Plumbing leak test & reports',
      'Electrical & Solar reports',
      'Asbestos sampling & reports',
      'Building reports'
    ]
  },
  {
    category: 'Construction Sequencing',
    icon: HardHat,
    items: [
      'Proper construction phase ordering',
      'Trade coordination best practices',
      'Site preparation guidelines',
      'Final inspection preparation'
    ]
  },
  {
    category: 'Cost Management',
    icon: FileText,
    items: [
      'Budget estimation templates',
      'Cost overrun prevention strategies',
      'Material selection guide',
      'Labor cost benchmarks'
    ]
  },
  {
    category: 'DIY Projects',
    icon: Hammer,
    items: [
      'DIY scope & feasibility review',
      'Tools & materials shortlist for home projects',
      'Step-by-step renovation playbook',
      'When to DIY vs. when to call a tradesperson',
      'Safety checklist for home builders',
      'Permit & council compliance for DIY works'
    ]
  },
  {
    category: 'Project Management',
    icon: BookOpen,
    items: [
      'Project timeline templates',
      'Stage-gate planning & milestone tracking',
      'Communication protocols',
      'Stakeholder coordination',
      'Risk management strategies',
      'Documentation best practices'
    ]
  },
  {
    category: 'Trade Works & Administration',
    icon: Wrench,
    comingSoon: true,
    items: [
      'Quoting & Scoping reviews',
      'Contracts & Conditions reviews',
      'Tenders & Costing reviews',
      'Programs & Timeline reviews',
      'Project Safety & Risk Plans reviews',
      'Construction Documentation reviews'
    ]
  }
];

export default function ResourceHub() {
  const { user } = useAuth();
  const gotoProtected = useProtectedNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      {user && <Navigation />}
      
      <div className={`max-w-7xl mx-auto px-6 py-8 ${user ? 'mt-16' : 'pt-20'}`}>
        {!user && (
          <div className="mb-8 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <BookOpen className="w-8 h-8 text-black" />
              <span className="text-xl font-bold tracking-tight" style={{fontFamily: 'Outfit'}}>All Build Consult</span>
            </div>
            <button
              onClick={() => window.location.href = '/'}
              className="px-6 py-2 bg-black text-white rounded-sm hover:bg-slate-800 transition-all"
            >
              Sign In
            </button>
          </div>
        )}

        <div className="mb-12">
          <h1 className="text-4xl font-bold tracking-tight text-black mb-4" style={{fontFamily: 'Outfit'}}>
            Services
          </h1>
          <p className="text-lg text-slate-600">
            The full range of construction consulting services offered by All Build Consult — organised by category.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {resources.map((resource) => {
            const Icon = resource.icon;
            const isComingSoon = !!resource.comingSoon;
            return (
              <div
                key={resource.category}
                data-testid={`services-card-${resource.category.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                className={`grid-border bg-white p-6 hover-lift relative ${isComingSoon ? 'border-red-300 ring-1 ring-red-200' : ''}`}
              >
                {isComingSoon && (
                  <span
                    data-testid="services-coming-soon-badge"
                    className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 bg-red-600 text-white text-[10px] uppercase font-bold tracking-wider rounded-sm shadow-sm"
                  >
                    Coming Soon
                  </span>
                )}
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 ${isComingSoon ? 'bg-red-600' : 'bg-blue-600'} rounded-sm flex items-center justify-center`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold pr-24" style={{fontFamily: 'Outfit'}}>
                    {resource.category}
                  </h3>
                </div>
                {isComingSoon && (
                  <p className="mb-3 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-sm p-2">
                    This service category is launching soon. Categories below preview the upcoming scope.
                  </p>
                )}
                <ul className={`space-y-2 ${isComingSoon ? 'opacity-70' : ''}`}>
                  {resource.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-slate-600">
                      <span className={isComingSoon ? 'text-red-600 mt-1' : 'text-blue-600 mt-1'}>•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="mt-12 grid-border bg-blue-600 text-white p-8 text-center" data-testid="resource-hub-cta">
          <h2 className="text-2xl font-bold mb-4" style={{fontFamily: 'Outfit'}}>
            Need Expert Guidance?
          </h2>
          <p className="text-blue-100 mb-6">
            Connect with our construction professionals for personalized advice
          </p>
          <button
            type="button"
            onClick={() => gotoProtected('/consultants')}
            data-testid="resource-hub-expert-cta"
            className="px-8 py-3 bg-white text-blue-600 rounded-sm hover:bg-slate-50 transition-all font-medium"
          >
            {user ? 'Browse Expert Consultants' : 'Sign in to browse experts'}
          </button>
        </div>
      </div>
    </div>
  );
}